import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '../utils/toast';
import { Edit, Trash2, Play, FileText, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ViewTrainingContentPage = () => {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const videoRef = useRef(null);
  const pdfViewStartTime = useRef(null);
  const [hasMarkedVisited, setHasMarkedVisited] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set());
  const [currentProgress, setCurrentProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState('untapped');
  const [topicAssessment, setTopicAssessment] = useState(null);
  const [assessmentAttempts, setAssessmentAttempts] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchContent();
    loadViewedSections();
    fetchTopicAssessment();
  }, [contentId]);

  // Update progress when content or viewedSections change
  useEffect(() => {
    if (content && user?.role === 'Staff') {
      const totalSections = getTotalSections();
      const progress = calculateProgress(viewedSections, totalSections);
      const status = determineStatus(progress);
      setCurrentProgress(progress);
      setCurrentStatus(status);
    }
  }, [content, viewedSections, user]);

  // Load viewed sections from localStorage
  const loadViewedSections = () => {
    const stored = localStorage.getItem(`viewed_sections_${contentId}`);
    if (stored) {
      setViewedSections(new Set(JSON.parse(stored)));
    }
  };

  // Save viewed sections to localStorage
  const saveViewedSections = (sections) => {
    localStorage.setItem(`viewed_sections_${contentId}`, JSON.stringify([...sections]));
  };

  // Calculate progress based on sections viewed
  const calculateProgress = (viewedSectionsSet, totalSections) => {
    if (totalSections === 0) return 0;
    return Math.round((viewedSectionsSet.size / totalSections) * 100);
  };

  // Determine status based on progress
  const determineStatus = (progress) => {
    if (progress === 0) return 'untapped';
    if (progress === 100) return 'completed';
    return 'in_progress';
  };

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to fetch content details');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData.role === 'Staff') {
        navigate('/staff/dashboard');
      } else {
        navigate('/training');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTopicAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/topic/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_assessment) {
        setTopicAssessment(response.data);
        
        // If staff/manager, fetch their attempts
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        if (userData.role === 'Staff' || userData.role === 'Reporting Manager') {
          try {
            const attemptsRes = await axios.get(`${API}/assessments/my-assessments`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            // Filter attempts for this questionnaire
            const allAssessments = attemptsRes.data.assessments || [];
            const thisAssessment = allAssessments.find(a => 
              a.questionnaire_id === response.data.mapping.questionnaire_id
            );
            
            if (thisAssessment) {
              setAssessmentAttempts(thisAssessment.attempts || []);
            }
          } catch (err) {
            console.error('Failed to fetch attempts:', err);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch chapter assessment:', error);
      // Don't show error toast - it's okay if there's no assessment
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this training chapter?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/training/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Training chapter deleted successfully');
      navigate('/training');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete content');
    }
  };

  const updateProgress = async (status, completionPercentage = null) => {
    // Only track progress for Staff users
    if (user?.role !== 'Staff') return;
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        training_topic_id: contentId,
        status: status
      };
      
      if (completionPercentage !== null) {
        payload.completion_percentage = completionPercentage;
      }
      
      await axios.put(
        `${API}/staff/progress/update`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Set flag to refresh dashboard on return
      localStorage.setItem('refreshDashboard', 'true');
      
      // Show success toast with smooth fade
      if (status === 'completed') {
        toast.success('🎉 Content completed! Progress updated.', {
          duration: 3000,
          style: { background: '#10b981', color: 'white' }
        });
      } else if (status === 'visited' && !hasMarkedVisited) {
        toast.info('📚 Progress tracked', { duration: 2000 });
        setHasMarkedVisited(true);
      }
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const handlePreview = (contentType, fileUrl, fileName, language, sectionIndex = null) => {
    console.log('🎬 handlePreview called:', { contentType, fileUrl, fileName, language, sectionIndex });
    
    // Validate fileUrl
    if (!fileUrl || fileUrl.trim() === '') {
      toast.error('Content not available. Please ensure S3 is configured in Integration Settings and content has been uploaded.', {
        duration: 6000,
        style: {
          background: '#FEE2E2',
          border: '1px solid #EF4444',
          color: '#991B1B'
        }
      });
      console.error('❌ Invalid file URL:', { contentType, fileUrl, fileName, language });
      return;
    }

    // Check if URL is valid (starts with http/https)
    if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
      toast.error('Invalid content URL. Please configure S3 Base URL in Integration Settings.', {
        duration: 6000,
        style: {
          background: '#FEE2E2',
          border: '1px solid #EF4444',
          color: '#991B1B'
        }
      });
      console.error('❌ URL does not start with http/https:', fileUrl);
      return;
    }

    console.log('✅ URL validation passed, opening preview with URL:', fileUrl);
    setPreviewData({ contentType, fileUrl, fileName, language, sectionIndex });
    setShowPreview(true);
    
    // Mark as visited when user opens content (first time only)
    if (user?.role === 'Staff' && !hasMarkedVisited) {
      const initialProgress = viewedSections.size > 0 ? calculateProgress(viewedSections, getTotalSections()) : 0;
      updateProgress('visited', initialProgress);
      setHasMarkedVisited(true);
    }
    
    // Start tracking PDF view time
    if (contentType === 'pdf') {
      pdfViewStartTime.current = Date.now();
    }
  };

  // Get total number of sections (for sectioned content) or 1 for standalone
  const getTotalSections = () => {
    if (!content) return 1;
    if (content.content_structure === 'sectioned') {
      return content.sections?.length || 0;
    }
    return 1; // Standalone content counts as 1 section
  };

  // Mark a section as viewed and update progress
  const markSectionViewed = (sectionIndex) => {
    if (user?.role !== 'Staff') return;
    
    const newViewedSections = new Set(viewedSections);
    newViewedSections.add(sectionIndex);
    setViewedSections(newViewedSections);
    saveViewedSections(newViewedSections);
    
    const totalSections = getTotalSections();
    const progress = calculateProgress(newViewedSections, totalSections);
    const status = determineStatus(progress);
    
    setCurrentProgress(progress);
    setCurrentStatus(status);
    
    // Update backend
    updateProgress(status, progress);
  };

  const handleVideoProgress = (e) => {
    const video = e.target;
    const percentComplete = (video.currentTime / video.duration) * 100;
    
    // Only mark as viewed when video is mostly watched (90%+)
    // This prevents premature completion marking
    if (percentComplete >= 90 && user?.role === 'Staff') {
      const sectionIndex = previewData?.sectionIndex ?? 0;
      // Check if not already viewed to avoid duplicate updates
      if (!viewedSections.has(sectionIndex)) {
        markSectionViewed(sectionIndex);
      }
    }
  };

  const handleVideoEnded = () => {
    // Mark section as viewed when video ends
    const sectionIndex = previewData?.sectionIndex ?? 0;
    markSectionViewed(sectionIndex);
  };

  const handlePdfViewClose = () => {
    // Calculate PDF viewing time
    if (pdfViewStartTime.current && user?.role === 'Staff') {
      const viewDuration = (Date.now() - pdfViewStartTime.current) / 1000; // in seconds
      
      // If user viewed PDF for more than 10 seconds, mark section as viewed
      if (viewDuration >= 10) {
        const sectionIndex = previewData?.sectionIndex ?? 0;
        markSectionViewed(sectionIndex);
      }
      
      pdfViewStartTime.current = null;
    }
  };

  // Check if URL is a YouTube or Vimeo embed
  const isEmbeddedVideo = (url) => {
    return url?.includes('youtube.com') || url?.includes('youtu.be') || url?.includes('vimeo.com');
  };

  // Convert YouTube/Vimeo URL to embed format
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube formats
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtube.com/embed/')) {
      return url; // Already in embed format
    }
    
    // Vimeo formats
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    
    return url; // Return as-is for direct video files
  };

  const renderContentPreview = () => {
    if (!previewData) return null;

    const { contentType, fileUrl, fileName } = previewData;

    if (contentType === 'video') {
      const isEmbedded = isEmbeddedVideo(fileUrl);
      const embedUrl = getEmbedUrl(fileUrl);

      if (isEmbedded) {
        // Render embedded video player (YouTube/Vimeo)
        return (
          <div className="w-full">
            <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={embedUrl}
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={fileName}
              />
            </div>
            <div className="mt-2 text-sm text-gray-600 text-center">
              <p>📊 Watch the video to track your progress</p>
              <p className="text-xs text-gray-500 mt-1">
                Note: For embedded videos, progress is tracked when you close this window after watching.
              </p>
            </div>
          </div>
        );
      } else {
        // Render HTML5 video player for direct video files
        return (
          <div className="w-full">
            <video 
              ref={videoRef}
              controls 
              className="w-full rounded-lg"
              style={{ maxHeight: '70vh' }}
              onTimeUpdate={handleVideoProgress}
              onEnded={handleVideoEnded}
              onError={(e) => {
                console.error('Video loading error:', e);
                toast.error('Unable to load video. Please check the video URL or try a different browser.', {
                  duration: 5000
                });
              }}
            >
              <source src={fileUrl} type="video/mp4" />
              <source src={fileUrl} type="video/webm" />
              <source src={fileUrl} type="video/ogg" />
              Your browser does not support the video tag.
            </video>
            <div className="mt-2 text-sm text-gray-600 text-center">
              <p>📊 Your progress is being tracked automatically</p>
            </div>
          </div>
        );
      }
    }

    if (contentType === 'pdf') {
      return (
        <div className="w-full h-[70vh]">
          <iframe
            src={fileUrl}
            className="w-full h-full rounded-lg border"
            title={fileName}
          />
          <div className="mt-2 text-sm text-gray-600 text-center">
            <p>📊 Viewing time is being tracked. Close when finished to update progress.</p>
          </div>
        </div>
      );
    }

    if (contentType === 'image') {
      return (
        <div className="w-full">
          <img 
            src={fileUrl} 
            alt={fileName}
            className="w-full rounded-lg"
            style={{ maxHeight: '70vh', objectFit: 'contain' }}
          />
        </div>
      );
    }

    return <p className="text-gray-600">Preview not available for this content type.</p>;
  };

  const handleBack = () => {
    if (user?.role === 'Staff') {
      navigate('/staff/dashboard');
    } else {
      navigate('/training');
    }
  };

  const canManage = user && ['Super Admin', 'Exlva Admin', 'Hospital Admin'].includes(user.role);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (!content) return null;

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Training Chapter</h1>
            <div className="flex space-x-2">
              {canManage && (
                <>
                  <Button onClick={() => navigate(`/training/${contentId}/edit`)} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button onClick={handleDelete} variant="outline" className="text-red-600 border-red-600">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                </>
              )}
              <Button onClick={handleBack} variant="outline">
                {user?.role === 'Staff' ? 'Back to Dashboard' : 'Back to Library'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl p-8 shadow-xl space-y-8">
          {/* Progress Banner for Staff */}
          {user?.role === 'Staff' && (
            <div className={`rounded-xl p-4 border-2 ${
              currentProgress === 100 
                ? 'bg-green-50 border-green-500' 
                : currentProgress > 0 
                  ? 'bg-blue-50 border-blue-500' 
                  : 'bg-gray-50 border-gray-300'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className={`text-lg font-bold ${
                      currentProgress === 100 ? 'text-green-800' : 'text-blue-800'
                    }`}>
                      {currentProgress === 100 ? '🎉 Training Completed!' : '📚 Your Progress'}
                    </h3>
                    <Badge className={`${
                      currentProgress === 100 
                        ? 'bg-green-500 text-white' 
                        : currentProgress > 0 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-400 text-white'
                    }`}>
                      {currentStatus.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full transition-all duration-500 ease-out ${
                          currentProgress === 100 ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${currentProgress}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-bold text-gray-700 min-w-[50px]">
                      {currentProgress}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {viewedSections.size} of {getTotalSections()} subchapter(s) completed
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[18px] font-semibold" style={{ color: '#0056B3' }}>Basic Information</h2>
              <Badge className={content.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                {content.status === 'published' ? 'Published' : 'Draft'}
              </Badge>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div><p className="text-sm text-gray-600">Title</p><p className="font-medium text-lg">{content.title}</p></div>
              <div className="md:col-span-2"><p className="text-sm text-gray-600">Description</p><p className="font-medium">{content.description}</p></div>
              <div><p className="text-sm text-gray-600">Structure</p><p className="font-medium">{content.content_structure === 'standalone' ? 'Standalone' : 'Subchapter-based'}</p></div>
              <div><p className="text-sm text-gray-600">Created By</p><p className="font-medium">{content.created_by_name}</p></div>
            </div>
          </div>

          {/* Standalone Content */}
          {content.content_structure === 'standalone' && (
            <div>
              <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>Content</h2>
              <div className="mb-4">
                <p className="text-sm text-gray-600">Content Type</p>
                <Badge className="bg-blue-100 text-blue-800 capitalize">{content.content_type}</Badge>
              </div>
              <div>
                <h3 className="font-medium mb-3">Available Languages ({content.language_versions?.length || 0})</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {content.language_versions?.map((lang, index) => {
                    const hasValidUrl = lang.file_url && lang.file_url.trim() !== '' && (lang.file_url.startsWith('http://') || lang.file_url.startsWith('https://'));
                    return (
                      <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-lg">{lang.language}</p>
                          </div>
                          <Badge className="bg-gray-100 text-gray-700 capitalize">{content.content_type}</Badge>
                        </div>
                        {!hasValidUrl && (
                          <p className="text-xs text-red-600 mb-2 bg-red-50 p-2 rounded">
                            ⚠️ Content not available. Please configure S3 and upload content.
                          </p>
                        )}
                        <Button 
                          onClick={() => handlePreview(content.content_type, lang.file_url, lang.file_name, lang.language, 0)}
                          className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          disabled={!hasValidUrl}
                        >
                          {content.content_type === 'video' && <Play className="w-4 h-4 mr-2" />}
                          {content.content_type === 'pdf' && <FileText className="w-4 h-4 mr-2" />}
                          {content.content_type === 'image' && <ImageIcon className="w-4 h-4 mr-2" />}
                          {hasValidUrl ? 'Click to View' : 'Not Available'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Subchapter-based Content */}
          {content.content_structure === 'sectioned' && (
            <div>
              <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>Subchapters ({content.sections?.length || 0})</h2>
              {content.sections?.map((section, sIndex) => (
                <div key={sIndex} className={`border rounded-lg p-6 mb-4 transition-all ${viewedSections.has(sIndex) ? 'bg-green-50 border-green-300' : 'bg-gray-50'}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-lg">Subchapter {sIndex + 1}: {section.section_title}</h3>
                        {viewedSections.has(sIndex) && (
                          <Badge className="bg-green-500 text-white">
                            ✓ Completed
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-600 mt-1">{section.section_description}</p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-800 capitalize">{section.content_type}</Badge>
                  </div>
                  <div className="mt-4">
                    <h4 className="font-medium mb-3">Available Languages ({section.language_versions?.length || 0})</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {section.language_versions?.map((lang, lIndex) => {
                        const hasValidUrl = lang.file_url && lang.file_url.trim() !== '' && (lang.file_url.startsWith('http://') || lang.file_url.startsWith('https://'));
                        return (
                          <div key={lIndex} className="bg-white rounded-lg p-4 border hover:shadow-md transition-shadow">
                            <div className="mb-3">
                              <p className="font-semibold">{lang.language}</p>
                            </div>
                            {!hasValidUrl && (
                              <p className="text-xs text-red-600 mb-2 bg-red-50 p-2 rounded">
                                ⚠️ Content not available. Please configure S3 and upload content.
                              </p>
                            )}
                            <Button 
                              onClick={() => handlePreview(section.content_type, lang.file_url, lang.file_name, lang.language, sIndex)}
                              className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90 disabled:bg-gray-400 disabled:cursor-not-allowed"
                              size="sm"
                              disabled={!hasValidUrl}
                            >
                              {section.content_type === 'video' && <Play className="w-4 h-4 mr-2" />}
                              {section.content_type === 'pdf' && <FileText className="w-4 h-4 mr-2" />}
                              {section.content_type === 'image' && <ImageIcon className="w-4 h-4 mr-2" />}
                              {hasValidUrl ? 'Click to View' : 'Not Available'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Assessment Subchapter */}
          {topicAssessment && topicAssessment.has_assessment && (
            <div className="border-t pt-8">
              <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>
                📝 Assessment for this Chapter
              </h2>
              
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border-2 border-purple-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-[22px] font-bold text-purple-900 mb-2">
                      {topicAssessment.questionnaire.title}
                    </h3>
                    <p className="text-gray-700 mb-4">{topicAssessment.questionnaire.description}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-600">Questions</p>
                        <p className="text-[22px] font-bold text-gray-900">{topicAssessment.questionnaire.total_questions}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-600">Total Points</p>
                        <p className="text-[22px] font-bold text-gray-900">{topicAssessment.questionnaire.total_points}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-600">Pass Score</p>
                        <p className="text-[22px] font-bold text-green-600">{topicAssessment.questionnaire.pass_score_percentage}%</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-600">Max Attempts</p>
                        <p className="text-[22px] font-bold text-gray-900">{topicAssessment.questionnaire.max_attempts}</p>
                      </div>
                    </div>

                    {topicAssessment.questionnaire.time_limit_minutes && (
                      <div className="flex items-center space-x-2 mb-4">
                        <Badge className="bg-orange-100 text-orange-800">
                          ⏱️ Time Limit: {topicAssessment.questionnaire.time_limit_minutes} minutes
                        </Badge>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      {topicAssessment.mapping.is_mandatory && (
                        <Badge className="bg-red-100 text-red-800">
                          ⚠️ Mandatory
                        </Badge>
                      )}
                      {topicAssessment.mapping.passing_required_for_completion && (
                        <Badge className="bg-yellow-100 text-yellow-800">
                          ✅ Passing Required for Completion
                        </Badge>
                      )}
                    </div>

                    {/* Show attempts info for staff */}
                    {(user?.role === 'Staff' || user?.role === 'Reporting Manager') && assessmentAttempts.length > 0 && (
                      <div className="mt-4 p-4 bg-white rounded-lg border">
                        <h4 className="font-semibold text-gray-900 mb-3">Your Attempts ({assessmentAttempts.length}/{topicAssessment.questionnaire.max_attempts})</h4>
                        <div className="space-y-2">
                          {assessmentAttempts.slice(0, 3).map((attempt, index) => (
                            <div key={attempt.attempt_id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                              <div className="flex items-center space-x-3">
                                <Badge className={attempt.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                  Attempt #{attempt.attempt_number}
                                </Badge>
                                <span className="text-sm font-medium">Score: {attempt.percentage}%</span>
                                <span className="text-sm text-gray-600">({attempt.total_score}/{attempt.max_score} points)</span>
                              </div>
                              {attempt.passed ? (
                                <Badge className="bg-green-500 text-white">✓ Passed</Badge>
                              ) : (
                                <Badge className="bg-red-500 text-white">✗ Failed</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Button */}
                {(user?.role === 'Staff' || user?.role === 'Reporting Manager') && (
                  <div className="mt-6">
                    {assessmentAttempts.length >= topicAssessment.questionnaire.max_attempts ? (
                      <div className="bg-gray-100 text-gray-700 p-4 rounded-lg text-center">
                        <p className="font-semibold">You have used all your attempts ({assessmentAttempts.length}/{topicAssessment.questionnaire.max_attempts})</p>
                        {assessmentAttempts.some(a => a.passed) ? (
                          <p className="text-green-600 mt-2">✓ You have passed this assessment!</p>
                        ) : (
                          <p className="text-red-600 mt-2">Please contact your administrator for more attempts.</p>
                        )}
                      </div>
                    ) : assessmentAttempts.some(a => a.passed) ? (
                      <div className="bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-lg text-center">
                        <p className="font-semibold text-lg">🎉 You have passed this assessment!</p>
                        <p className="text-sm mt-2">You may retake it to improve your score if you wish.</p>
                        <Button 
                          onClick={() => navigate(`/assessment/take/${topicAssessment.questionnaire.questionnaire_id}`)}
                          className="mt-4 bg-green-600 hover:bg-green-700"
                        >
                          Retake Assessment
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        onClick={() => navigate(`/assessment/take/${topicAssessment.questionnaire.questionnaire_id}`)}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg font-semibold"
                        size="lg"
                      >
                        {assessmentAttempts.length > 0 ? 'Retake Assessment' : 'Start Assessment'} 
                        <span className="ml-2">→</span>
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Content Preview Modal */}
      <Dialog 
        open={showPreview} 
        onOpenChange={(isOpen) => {
          setShowPreview(isOpen);
          // When closing the modal, track completion for PDF and embedded videos
          if (!isOpen) {
            if (previewData?.contentType === 'pdf') {
              handlePdfViewClose();
            } else if (previewData?.contentType === 'video' && isEmbeddedVideo(previewData?.fileUrl)) {
              // For embedded videos, mark as viewed when modal closes
              // (we can't track playback events from YouTube/Vimeo iframes)
              const sectionIndex = previewData?.sectionIndex ?? 0;
              if (user?.role === 'Staff' && !viewedSections.has(sectionIndex)) {
                // Assume user watched if they had the video open for a while
                setTimeout(() => {
                  markSectionViewed(sectionIndex);
                  toast.success('Video chapter marked as viewed!', { duration: 2000 });
                }, 500);
              }
            }
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {previewData?.language} - {content.title}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 mt-4">
          <div className="mt-4">
            {renderContentPreview()}
          </div>
          
          {/* Display Video URL for debugging */}
          {previewData && (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-700 mb-1">Content URL:</p>
                  <p className="text-xs text-gray-600 break-all font-mono bg-white p-2 rounded border">
                    {previewData.fileUrl}
                  </p>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(previewData.fileUrl);
                    toast.success('URL copied to clipboard!', { duration: 2000 });
                  }}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
                >
                  Copy URL
                </button>
              </div>
              <a
                href={previewData.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                Open URL in new tab →
              </a>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ViewTrainingContentPage;